import { z } from "zod";
import { normalizeUnitNumber } from "./units";

/**
 * The password policy for every credential set in this system: >=12 characters with
 * letters and digits. Mirrors the server's rule in `server/src/routes/auth.routes.ts`.
 * Since the administration now issues every account, an admin-chosen password is the
 * only human-picked credential there is — it must not be the weakest link.
 */
export const strongPasswordSchema = z
  .string()
  .min(12, "La contraseña debe tener al menos 12 caracteres")
  .refine((p) => /[A-Za-z]/.test(p) && /\d/.test(p), "Incluya letras y números");

export const loginSchema = z.object({
  email: z.string().email("Correo electrónico inválido"),
  // Deliberately loose: this validates an EXISTING password. Tightening it here would
  // lock out anyone whose current password predates the policy above.
  password: z.string().min(8, "La contraseña debe tener al menos 8 caracteres"),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().email("Correo electrónico inválido"),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(10, "Token inválido"),
  password: strongPasswordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const refreshSchema = z.object({
  refreshToken: z.string().min(10),
});

export const roleEnum = z.enum(["ADMIN", "BOARD_MEMBER", "RESIDENT", "PUBLIC"]);
// The roles an account can be given. PUBLIC still exists for older accounts (who get an
// owner's view), but is no longer assigned.
export const assignableRoleEnum = z.enum(["ADMIN", "BOARD_MEMBER", "RESIDENT"], {
  errorMap: () => ({ message: "Rol no válido: elija Administración, Junta o Titular" }),
});

export const createUserSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1, "Nombre requerido"),
  lastName: z.string().min(1, "Apellido requerido"),
  phone: z.string().max(40).optional().nullable(),
  role: assignableRoleEnum.default("RESIDENT"),
  unitId: z.string().optional().nullable(),
  // Optional: omit it and the server generates a random temp password instead.
  password: strongPasswordSchema.optional(),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = createUserSchema.partial().extend({
  isActive: z.boolean().optional(),
  permissions: z.array(z.string()).optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const boardPositionEnum = z.enum(["PRESIDENT", "VICE_PRESIDENT", "SECRETARY", "TREASURER", "DIRECTOR"]);

// PUT /users/:id/board-position — `position: null` removes the seat.
export const setBoardPositionSchema = z.object({
  position: boardPositionEnum.nullable(),
  termStart: z.coerce.date().optional(),
  termEnd: z.coerce.date().optional(),
});
export type SetBoardPositionInput = z.infer<typeof setBoardPositionSchema>;

// POST /units/provision-owners — create a portal login for every unit's titular.
// dryRun (default) only reports what would happen.
export const provisionOwnersSchema = z.object({
  dryRun: z.boolean().default(true),
  unitIds: z.array(z.string().min(1)).optional(),
});
export type ProvisionOwnersInput = z.infer<typeof provisionOwnersSchema>;

export const createUnitSchema = z.object({
  // Stored normalised (uppercase, no inner spaces) so "e-1" and "E-1" are the same unit.
  unitNumber: z.string().trim().min(1, "Número de unidad requerido").transform(normalizeUnitNumber),
  // Standard visible code (CD1-E-01). Empty/omitted → derived from the unit number.
  code: z.string().trim().max(30).optional().nullable(),
  // USPS mailing address (labelled "Dirección USPS" in the portal).
  officialAddress: z.string().trim().max(200).optional().nullable(),
  building: z.string().optional().nullable(),
  floor: z.string().optional().nullable(),
  bedrooms: z.coerce.number().int().min(0).max(10).optional().nullable(),
  // General participation coefficient from the Escritura Matriz, as a fraction
  // (e.g. 0.01091 = 1.091%). Basis for assembly vote weight and expense allocation.
  participationPct: z.coerce.number().min(0).max(1).optional().nullable(),
  // Monthly maintenance fee billed to the unit (USD). Bulk fee generation bills it as-is.
  monthlyFee: z.coerce.number().min(0).max(100000).optional().nullable(),
  ownerName: z.string().min(1, "Nombre del titular requerido"),
  ownerEmail: z.string().email().optional().nullable(),
  ownerPhone: z.string().optional().nullable(),
  // Second contact — kept on file only; system emails go to ownerEmail.
  ownerEmail2: z.string().email("Segundo correo inválido").optional().nullable(),
  ownerPhone2: z.string().optional().nullable(),
  isOccupied: z.boolean().default(true),
});
export type CreateUnitInput = z.infer<typeof createUnitSchema>;

export const updateUnitSchema = createUnitSchema.partial();
export type UpdateUnitInput = z.infer<typeof updateUnitSchema>;

// The tenant living in a unit (announcements reach them; fee emails never do).
const optionalEmail = z
  .string()
  .trim()
  .transform((v) => v || null)
  .pipe(z.string().email("Correo inválido").nullable())
  .optional()
  .nullable();
const optionalText = z.string().trim().max(200).optional().nullable();
export const tenantSchema = z
  .object({
    name: z.string().trim().min(1, "Nombre del inquilino requerido").max(200),
    email: optionalEmail,
    email2: optionalEmail,
    phone: optionalText,
    phone2: optionalText,
    notes: z.string().trim().max(1000).optional().nullable(),
    startDate: z.coerce.date(),
  });
export const updateTenantSchema = tenantSchema.partial();
export const endTenantSchema = z.object({ endDate: z.coerce.date() });
